import React, { useState } from "react";
import {
  X, GraduationCap, Award, Check, ArrowRight, Info, ShieldCheck,
} from "lucide-react";
import { Button } from "./ui/button";

const CFU_ADDON = 40;

/**
 * Popup certificazioni corso.
 * Chiede DUE domande obbligatorie:
 *  1) Vuoi certificare il corso via L.4/2013 (Aggregazione MIMIT – Centro Studi Esse)? SI/NO (gratis)
 *  2) Vuoi certificare per riconoscimento universitario e ottenere CFU? SI/NO (+40€)
 *
 * Alla conferma chiama onConfirm({ cert_l4, cert_cfu, prezzoFinale })
 */
export default function CertificazioniModal({ prezzoBase, packageName, onClose, onConfirm }) {
  const [certL4, setCertL4] = useState(null);
  const [certCfu, setCertCfu] = useState(null);

  const canConfirm = certL4 !== null && certCfu !== null;
  const prezzoFinale = prezzoBase + (certCfu ? CFU_ADDON : 0);

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({ cert_l4: !!certL4, cert_cfu: !!certCfu, prezzoFinale });
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="certificazioni-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl my-8 shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#0a1f55] to-[#102a6b] text-white p-6 relative">
          <button onClick={onClose} data-testid="cert-close"
            className="absolute top-4 right-4 h-9 w-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b] text-[#0a1020] text-[10.5px] font-extrabold uppercase tracking-[0.18em] mb-3">
            <Award className="h-3.5 w-3.5" /> Prima del pagamento
          </div>
          <h2 className="text-xl md:text-2xl font-extrabold">Vuoi certificare il corso?</h2>
          <p className="text-[13.5px] text-slate-200 mt-1">Rispondi a 2 domande. Le certificazioni sono opzionali ma consigliate.</p>
        </div>

        <div className="p-6 space-y-5">
          {/* Domanda 1 - L. 4/2013 */}
          <div className="rounded-2xl border-2 border-slate-200 p-5" data-testid="cert-question-l4">
            <div className="flex items-start gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl bg-[#102a6b] text-white flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-[#102a6b]">Domanda 1 · Gratis</div>
                <h3 className="font-extrabold text-[#0a1f55] text-[15px]">Vuoi la certificazione L. 4/2013?</h3>
                <p className="text-[13px] text-slate-600 mt-1">
                  Riconoscimento tramite <strong>Aggregazione MIMIT via Centro Studi Esse</strong>.
                  Titolo valido come attestato di qualità professionale.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <OptionButton active={certL4 === true} onClick={() => setCertL4(true)} label="Sì, la voglio" tone="primary" testid="cert-l4-yes" />
              <OptionButton active={certL4 === false} onClick={() => setCertL4(false)} label="No, grazie" tone="ghost" testid="cert-l4-no" />
            </div>
          </div>

          {/* Domanda 2 - CFU */}
          <div className={`rounded-2xl border-2 p-5 transition-all ${certCfu ? "border-[#f59e0b] bg-amber-50/50" : "border-slate-200"}`} data-testid="cert-question-cfu">
            <div className="flex items-start gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl bg-[#f59e0b] text-white flex items-center justify-center flex-shrink-0">
                <GraduationCap className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-[#b45309]">Domanda 2 · +€{CFU_ADDON}</div>
                </div>
                <h3 className="font-extrabold text-[#0a1f55] text-[15px]">Vuoi la certificazione universitaria per CFU?</h3>
                <p className="text-[13px] text-slate-600 mt-1">
                  Riconoscimento universitario per ottenere i <strong>Crediti Formativi Universitari (CFU)</strong>.
                  Richiede procedura aggiuntiva presso l&apos;università partner (+€{CFU_ADDON} una tantum).
                </p>
                {certCfu && (
                  <div className="mt-3 rounded-xl bg-white border border-amber-200 p-3 text-[12.5px] text-amber-900 flex items-start gap-2">
                    <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <span>Dopo il pagamento la segreteria attiverà la procedura di riconoscimento CFU. Riceverai aggiornamenti via email e nella Dashboard.</span>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <OptionButton active={certCfu === true} onClick={() => setCertCfu(true)} label={`Sì, aggiungi (+€${CFU_ADDON})`} tone="accent" testid="cert-cfu-yes" />
              <OptionButton active={certCfu === false} onClick={() => setCertCfu(false)} label="No, grazie" tone="ghost" testid="cert-cfu-no" />
            </div>
          </div>

          {/* Riepilogo prezzo */}
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
            <div className="flex items-center justify-between text-[13px] mb-1">
              <span className="text-slate-600">{packageName}</span>
              <span className="font-bold text-[#0a1f55]">€ {prezzoBase.toFixed(2)}</span>
            </div>
            {certCfu && (
              <div className="flex items-center justify-between text-[13px] mb-1" data-testid="riepilogo-cfu-row">
                <span className="text-amber-700">+ Certificazione CFU universitaria</span>
                <span className="font-bold text-amber-700">€ {CFU_ADDON.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-200">
              <span className="font-extrabold text-[#0a1f55]">Totale</span>
              <span className="font-extrabold text-2xl text-[#0a1f55]" data-testid="riepilogo-totale">€ {prezzoFinale.toFixed(2)}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="cert-annulla" className="border-slate-300">Annulla</Button>
            <Button
              disabled={!canConfirm}
              onClick={handleConfirm}
              data-testid="cert-conferma"
              className="flex-1 h-12 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold disabled:opacity-50"
            >
              Prosegui al pagamento € {prezzoFinale.toFixed(2)} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          {!canConfirm && (
            <p className="text-[11.5px] text-center text-slate-500">Rispondi a entrambe le domande per procedere</p>
          )}
        </div>
      </div>
    </div>
  );
}

function OptionButton({ active, onClick, label, tone, testid }) {
  const styles = {
    primary: active
      ? "border-[#102a6b] bg-[#102a6b] text-white shadow"
      : "border-slate-200 bg-white text-slate-700 hover:border-[#102a6b]/40",
    accent: active
      ? "border-[#f59e0b] bg-[#f59e0b] text-[#0a1020] shadow"
      : "border-slate-200 bg-white text-slate-700 hover:border-[#f59e0b]/40",
    ghost: active
      ? "border-slate-500 bg-slate-100 text-slate-800 shadow"
      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300",
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`h-12 rounded-xl border-2 font-bold text-[13.5px] transition-all inline-flex items-center justify-center gap-2 ${styles[tone]}`}
    >
      {active && <Check className="h-4 w-4" />}
      {label}
    </button>
  );
}

export const CFU_ADDON_EUR = CFU_ADDON;
export const PACCHETTI_CORSO = ["only-course", "tessera-corso", "premium"];
